import React, { useEffect, useRef, useState } from 'react';
import useTomSelect from '../../hooks/useTomSelect';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import { validateField } from '../../utils/validation';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSearch } from '../../hooks/useSearch';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';

const Contacts = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();
  
  // Helper function to check contacts-specific capabilities
  const canContacts = (capabilityKey) => can(capabilityKey, 'contacts');

  // Pagination
  const pagination = usePagination(10);

  // State for managing form data
  const [selectedIds, setSelectedIds] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'desc' });

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    description: '',
    allowLogin: false,
    generatePassword: false,
    password: '',
    confirmPassword: ''
  });

  const handleCreate = () => {
    if (!canContacts('create')) {
      setFailMessage('You do not have permission to create contacts.');
      setShowFailModal(true);
      return;
    }
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      description: '',
      allowLogin: false,
      generatePassword: false,
      password: '',
      confirmPassword: ''
    });
    setErrors({ contacts: [{}] });
    setSubmitted(false);
    setEditingId(null); // Reset ke mode "Create"
    // setShowModal(true); // Munculkan modal
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = new window.bootstrap.Modal(document.getElementById("modal-contact"), {
        backdrop: 'static',
        keyboard: true
      });
      modal.show();
    }
  };

  const [companies, setCompanies] = useState([]);
  const companySelectRef = useRef();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await api.get('/md/companies');
        setCompanies(response.data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompanies();
  }, []);

  // State for tracking validation errors
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);

  const tagsSelectRef = useTomSelect({
    plugins: ['remove_button'],
    persist: false,
    createOnBlur: true,
    create: true,
    onChange: (value) => handleSelectChange('tags', value)
  });

  // Function to handle select changes (TomSelect)
  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Skip validation for fields populated by other means
    if (['company'].includes(name)) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      return;
    }

    // Validate field in real-time
    const error = validateField(name, value, {
      required: true,
      type: getFieldType(name),
      minLength: getMinLength(name),
      maxLength: getMaxLength(name)
    });

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Helper function to determine field type
  const getFieldType = (fieldName) => {
    const typeMap = {
      email: 'email',
      phone: 'phone',
      password: 'password',
      confirmPassword: 'password'
    };
    return typeMap[fieldName] || 'text';
  };

  // Helper function to get min length for fields
  const getMinLength = (fieldName) => {
    const minLengthMap = {
      firstName: 2,
      lastName: 2,
      password: 8
    };
    return minLengthMap[fieldName];
  };

  // Helper function to get max length for fields
  const getMaxLength = (fieldName) => {
    const maxLengthMap = {
      firstName: 50,
      lastName: 50,
      phone: 20
    };
    return maxLengthMap[fieldName];
  };

  // Function to handle login toggle
  const handleLoginToggle = (value) => {
    setFormData((prev) => ({
      ...prev,
      allowLogin: value,
      // Reset password fields when disabling login
      ...(value === false && {
        password: '',
        confirmPassword: '',
        generatePassword: false
      })
    }));

    // Clear password errors when disabling login
    if (!value) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.password;
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  };

  // Function to handle generate password toggle
  const handleGeneratePasswordToggle = (value) => {
    const generateRandomPassword = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    };

    const generatedPassword = value ? generateRandomPassword() : '';

    setFormData((prev) => ({
      ...prev,
      generatePassword: value,
      password: generatedPassword,
      confirmPassword: generatedPassword
    }));

    // Clear password errors when enabling generate password
    if (value) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.password;
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  };

  // Handle password validation
  const validatePasswords = (password, confirmPassword) => {
    const newErrors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation with validateField utility
    const fields = {
      firstName: { required: true, type: 'text', minLength: 2 },
      lastName: { required: true, type: 'text', minLength: 2 },
      position: { required: true, type: 'text' },
      email: { required: true, type: 'email' },
      phone: { required: true, type: 'phone' },
      company: { required: true, type: 'text' }
    };

    // Validate each field
    Object.entries(fields).forEach(([field, options]) => {
      const error = validateField(field, formData[field], options);
      if (error) newErrors[field] = error;
    });

    // Password validation if login is allowed
    if (formData.allowLogin && !formData.generatePassword) {
      const passwordErrors = validatePasswords(formData.password, formData.confirmPassword);
      Object.assign(newErrors, passwordErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validateForm()) {
      return;
    }

    const payload = {
      companyId: Number(formData.company), // Convert string back to number
      position: formData.position,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      allowLogin: formData.allowLogin,
      confirmPassword: formData.confirmPassword
    };

    // Add login information to payload if allowLogin is true
    if (formData.allowLogin) {
      payload.allowLogin = true;
      if (formData.generatePassword) {
        payload.generatePassword = true;
      } else {
        payload.password = formData.password;
      }
    }

    try {
      if (editingId) {
        await api.put(`/md/contact/${editingId}`, payload);
        setSuccessMessage('Contact updated successfully');
      } else {
        await api.post('/md/contact', payload);
        setSuccessMessage('Contact created successfully');
      }

      setShowSuccessModal(true);
      fetchContacts();

      setFormData({
        company: '',
        position: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        allowLogin: false,
        generatePassword: false,
        password: '',
        confirmPassword: ''
      });

      setSubmitted(false);
      setErrors({});
      setEditingId(null);

      const modal = document.getElementById('modal-contact');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }

    } catch (error) {
      console.error('Error saving contact:', error);
      let errorMessage = 'Failed to save contact.';

      // Handle specific error cases
      if (error.response) {
        switch (error.response.status) {
          case 409:
            errorMessage = 'A contact with this email already exists.';
            break;
          case 400:
            errorMessage = error.response.data.message || 'Invalid data provided.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          default:
            errorMessage = 'An error occurred while saving the contact.';
        }
      }

      setFailMessage(errorMessage);
      setShowFailModal(true);
    }
  };

  useEffect(() => {
    // Initialize Litepicker if available for date filters
      if (window.Litepicker) {
        const calendarIconPrev = `
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-1">
            <path d="M15 6l-6 6l6 6" />
          </svg>`;

        const calendarIconNext = `
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-1">
            <path d="M9 6l6 6l-6 6" />
          </svg>`;

        ['datepicker-from', 'datepicker-to'].forEach(id => {
          const element = document.getElementById(id);
          if (element && !element.litepicker) {
            new window.Litepicker({
              element: element,
              buttonText: {
                previousMonth: calendarIconPrev,
                nextMonth: calendarIconNext
              }
            });
          }
        });
      }
  }, []);


  const { search, handleSearchChange } = useSearch((value) => {
    pagination.setPage(0);
    fetchContacts(value);
  });

  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
    handleSearchChange(value);
  };

  const fetchContacts = async (searchTerm = '', sort = null) => {
    try {
      const params = { 
        page: pagination.page, 
        size: pagination.size, 
        search: searchTerm 
      };
      
      // Add sort parameters - default to created date descending if not specified
      const activeSort = sort || sortConfig || { key: 'created', direction: 'desc' };
      if (activeSort && activeSort.key) {
        params.sortBy = activeSort.key;
        params.sortDir = activeSort.direction;
      }
      
      const response = await api.get('/md/contacts-page-2', { params });
      setContacts(response.data.content);
      pagination.setTotalPages(response.data.totalPages);
      pagination.setTotal(response.data.totalElements);
      pagination.setCurrentPageItems(response.data.content.length);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  useEffect(() => {
    fetchContacts(searchTerm, sortConfig);
  }, [pagination.page, pagination.size, searchTerm, sortConfig]);

  // Date formatter function for dd/mm/yyyy format
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Define columns for GenericView table
  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true
    },
    {
      key: 'company',
      label: 'Company',
      sortable: true
    },
    {
      key: 'position',
      label: 'Position',
      sortable: true
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true
    },
    {
      key: 'created',
      label: 'Created Date',
      sortable: true,
      type: 'date',
      render: (value) => formatDate(value)
    }
  ];

  const handleDelete = async () => {
    if (!canContacts('delete')) {
      setFailMessage('You do not have permission to delete contacts.');
      setShowFailModal(true);
      return;
    }
    if (selectedIds.length === 0) {
      alert('Please select items to delete');
      return;
    }
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete('/md/contacts', { data: selectedIds });
      setShowDeleteModal(false);
      setSuccessMessage(`Successfully deleted ${selectedIds.length} contact(s)`);
      setShowSuccessModal(true);
      fetchContacts();
      setSelectedIds([]);
    } catch (error) {
      console.error('Error deleting contacts:', error);
    }
  };

  const handleEdit = () => {
    if (!canContacts('edit')) {
      setFailMessage('You do not have permission to edit contacts.');
      setShowFailModal(true);
      return;
    }
    if (selectedIds.length !== 1) {
      alert("Please select one contact to edit");
      return;
    }

    const selectedContact = contacts.find((c) => c.id === selectedIds[0]);
    if (!selectedContact) return;

    // Find the company by name and get its ID
    const company = companies.find(c => c.companyName === selectedContact.company);

    setFormData({
      company: company ? String(company.id) : '', // Convert to string since TomSelect expects string values
      position: selectedContact.position || '',
      firstName: selectedContact.name?.split(" ")[0] || '',
      lastName: selectedContact.name?.split(" ")[1] || '',
      email: selectedContact.email || '',
      phone: selectedContact.phone || '',
      allowLogin: false,
      generatePassword: false,
      password: '',
      confirmPassword: ''
    });

    setEditingId(selectedContact.id);
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = new window.bootstrap.Modal(document.getElementById("modal-contact"), {
        backdrop: 'static',
        keyboard: true
      });
      modal.show();
    }
  };


  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">
                Contacts
              </h2>
            </div>
            {/* Page title actions */}
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                {canContacts('create') && (
                  <>
                    <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                      // data-bs-toggle="modal" data-bs-target="#modal-contact"
                      onClick={handleCreate}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                        strokeLinejoin="round" className="icon">
                        <path d="M12 5l0 14" />
                        <path d="M5 12l14 0" />
                      </svg>
                      Create
                    </a>
                    <a href="#" className="btn btn-primary d-sm-none btn-icon" data-bs-toggle="modal"
                      data-bs-target="#modal-contact" aria-label="Create new contact">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                        strokeLinejoin="round" className="icon">
                        <path d="M12 5l0 14" />
                        <path d="M5 12l14 0" />
                      </svg>
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <GenericView
                  data={contacts}
                  columns={columns}
                  isLoading={loading || capabilitiesLoading}
                  emptyStateMessage="No contacts found"
                  checkboxSelection={true}
                  selectedRows={selectedIds}
                  onSelectRow={(itemId, isSelected) => {
                    if (isSelected) {
                      setSelectedIds([...selectedIds, itemId]);
                    } else {
                      setSelectedIds(selectedIds.filter(id => id !== itemId));
                    }
                  }}
                  onSelectAllRows={(itemIds) => {
                    if (Array.isArray(itemIds) && itemIds.length > 0) {
                      setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                  onEdit={canContacts('edit') ? (item) => {
                    setSelectedIds([item.id]);
                    handleEdit();
                  } : undefined}
                  onDelete={canContacts('delete') ? (item) => {
                    setSelectedIds([item.id]);
                    handleDelete();
                  } : undefined}
                  searchComponent={
                    <div className="d-flex gap-2">
                      <SearchInput
                          value={searchTerm}
                        onChange={handleSearchInputChange}
                        placeholder="Search..."
                        uniqueId="contacts-search"
                        />
                        <a className="btn btn-icon" data-bs-toggle="offcanvas" href="#offcanvasEnd"
                          role="button" aria-controls="offcanvasEnd">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-adjustments">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M4 10a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                            <path d="M6 4v4" />
                            <path d="M6 12v8" />
                            <path d="M10 16a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                            <path d="M12 4v10" />
                            <path d="M12 18v2" />
                            <path d="M16 7a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                            <path d="M18 4v1" />
                            <path d="M18 9v11" />
                          </svg>
                        </a>
                      </div>
                  }
                  actionButtons={
                    <div className="d-flex gap-2">
                      {(canContacts('edit') || canContacts('delete')) && (
                        <div ref={actionsContainerRef} className="dropdown">
                          <a
                            href="#"
                            ref={actionsToggleRef}
                            className="btn dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded={isActionsOpen}
                            onClick={(e) => {
                              e.preventDefault();
                              const inst = actionsDropdownInstanceRef.current;
                              if (inst?.toggle) inst.toggle();
                              else setIsActionsOpen(prev => !prev);
                            }}
                          >Actions</a>
                          <div className="dropdown-menu">
                            {canContacts('edit') && (
                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleEdit(); }}
                                style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                Edit
                              </a>
                            )}
                            {canContacts('delete') && (
                              <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleDelete(); }}
                                style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                Delete
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  }
                  pagination={{
                    currentPage: pagination.page + 1, // GenericView uses 1-based pages
                    totalRows: pagination.total,
                    rowsPerPage: pagination.size,
                    onPageChange: (page) => pagination.setPage(page - 1), // Convert back to 0-based
                    onRowsPerPageChange: (size) => {
                      pagination.setSize(size);
                      pagination.setPage(0);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  uniqueId="contacts-table"
                  defaultSortConfig={{ key: 'created', direction: 'desc' }}
                  onSortChange={(newSortConfig) => {
                    setSortConfig(newSortConfig);
                    pagination.setPage(0); // Reset to first page when sorting changes
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Contact Modal */}
      <div className="modal modal-blur fade" id="modal-contact" tabIndex="-1" role="dialog"
        aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              {/* <h5 className="modal-title">{isEditMode ? "Edit Contact" : "New Contact"}</h5> */}
              <h5 className="modal-title">{editingId ? "Edit Contact" : "New Contact"}</h5>

              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-lg-6">

                  <div className="mb-3">
                    <label className="form-label">
                      Company <span className="text-danger">*</span>
                    </label>
                    <TomSelect
                      name="company"
                      value={formData.company === undefined || formData.company === null ? '' : String(formData.company)}
                      onChange={(name, value) => handleInputChange({ target: { name, value } })}
                      options={companies.map(company => ({
                        id: company.id,
                        name: company.companyName
                      }))}
                      placeholder="Select Company"
                      required={true}
                      error={submitted && errors.company ? errors.company : null}
                    />
                  </div>

                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">Position <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${submitted && errors.position ? 'is-invalid' : ''}`}
                      name="position"
                      placeholder="Enter position"
                      value={formData.position}
                      onChange={handleInputChange}
                    />
                    {submitted && errors.position && <div className="invalid-feedback">{errors.position}</div>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">First Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${submitted && errors.firstName ? 'is-invalid' : ''}`}
                      name="firstName"
                      placeholder="Enter first name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                    />
                    {submitted && errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">Last Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${submitted && errors.lastName ? 'is-invalid' : ''}`}
                      name="lastName"
                      placeholder="Enter last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                    />
                    {submitted && errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      name="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">Phone <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                      name="phone"
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="mb-3">
                    <label className="form-label">Allow Login Access</label>
                    <label className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.allowLogin || false}
                        onChange={(e) => handleLoginToggle(e.target.checked)}
                      />
                      <span className="form-check-label">Enable user account login</span>
                    </label>
                  </div>
                </div>

                {formData.allowLogin && (
                  <>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Generate Random Password</label>
                        <label className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.generatePassword || false}
                            onChange={(e) => handleGeneratePasswordToggle(e.target.checked)}
                          />
                          <span className="form-check-label">Auto-generate secure password</span>
                        </label>
                        {formData.generatePassword && (
                          <div className="form-hint text-success">
                            A secure password will be generated and sent to the user's email
                          </div>
                        )}
                      </div>
                    </div>

                    {!formData.generatePassword && (
                      <>
                        <div className="col-lg-6">
                          <div className="mb-3">
                            <label className="form-label">Password <span className="text-danger">*</span></label>
                            <div className="input-group">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                name="password"
                                value={formData.password || ''}
                                onChange={handleInputChange}
                                placeholder="Enter password"
                              />
                              <span className="input-group-text cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                    <path d="M3 3l18 18"></path>
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                  </svg>
                                )}
                              </span>
                              {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="mb-3">
                            <label className="form-label">Confirm Password <span className="text-danger">*</span></label>
                            <div className="input-group">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                name="confirmPassword"
                                value={formData.confirmPassword || ''}
                                onChange={handleInputChange}
                                placeholder="Confirm password"
                              />
                              <span className="input-group-text cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                    <path d="M3 3l18 18"></path>
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                  </svg>
                                )}
                              </span>
                              {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                            </div>
                            {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                              <div className="invalid-feedback d-block">Passwords do not match</div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <a href="#" className="btn btn-link link-secondary"
                data-bs-dismiss="modal">Cancel</a>
              <a href="#" className="btn btn-primary ms-auto" id="createBtn" onClick={handleSubmit}>
                Save
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Offcanvas */}
      <div className="offcanvas offcanvas-end" tabIndex="-1" id="offcanvasEnd" aria-labelledby="offcanvasEndLabel">
        <div className="offcanvas-header">
          <h2 className="offcanvas-title" id="offcanvasEndLabel">Filter</h2>
          <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body">
          <div className="accordion" id="accordion-filter">
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={setFromDate}
              onToDateChange={setToDate}
            />

            <div className="accordion-item">
              <h2 className="accordion-header" id="heading-2">
                <button className="accordion-button collapsed" type="button"
                  data-bs-toggle="collapse" data-bs-target="#collapse-company"
                  aria-expanded="false">
                  Company
                </button>
              </h2>
              <div id="collapse-company" className="accordion-collapse collapse"
                data-bs-parent="#accordion-filter">
                <div className="accordion-body pt-0">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <TomSelect
                        name="filterCompanies"
                        value={[]}
                        onChange={() => { }}
                        options={companies.map(company => ({
                          id: company.id,
                          name: company.companyName
                        }))}
                        placeholder="Select companies"
                        create={false}
                        multiple={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="offcanvas-footer">
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-link link-secondary" onClick={() => {
              setFromDate('');
              setToDate('');
            }}>Reset</button>
            <button type="button" className="btn btn-primary me-2" data-bs-dismiss="offcanvas">Apply</button>
          </div>
        </div>
      </div>

      {/* Add the modals at the end of the component */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Contacts"
        message={`Are you sure you want to delete ${selectedIds.length} selected contact(s)?`}
        confirmText="Delete"
        type="danger"
      />
    </>
  );
};

export default Contacts; 